// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { ActionMenuItem } from '@/components/action-menu-item';
import { ArchivedIcon } from '@/components/archived-icon';
import {
  ArrowDropDownRounded,
  Delete,
  DownloadRounded,
  PlayArrowRounded,
  RefreshRounded,
  StopRounded,
  Sync,
} from '@/constants/hardfin-standard-icons';
import { useToast } from '@/contexts/toast-context';
import { useDisclosure } from '@/hooks/use-disclosure';
import {
  HubspotResyncProject,
  hubSpotResyncProject,
} from '@/project/api/hubspot-resync-project';
import {
  SfdcResyncProject,
  sfdcResyncProject,
} from '@/project/api/sfdc-resync-project';
import { Project } from '@/project/api/types';
import { CreateProjectDialog } from '@/project/components/create-project-dialog';
import { DeleteProjectDialog } from '@/project/components/delete-project-dialog';
import { EnableWorkflowDialog } from '@/project/components/enable-workflow-dialog';
import { useUpdateProject } from '@/project/hooks/use-update-project';
import { Box, Button, Menu } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useRef } from 'react';

interface Props {
  project: Project;
  isCRMEnabled: boolean;
  queryKey: string[];
}

export function ProjectActionButton(props: Props) {
  const menu = useDisclosure();
  const createDialog = useDisclosure();
  const deleteDialog = useDisclosure();
  const enableWorkflowDialog = useDisclosure();
  const ref = useRef<HTMLButtonElement>(null);

  const { mutate, isPending } = useUpdateProject(props.project.key);

  const archiveControlTitle = props.project.isArchived
    ? 'Unarchive project'
    : 'Archive project';

  const archiveControlDescription = props.project.isArchived
    ? 'Unarchiving shows this project in the list again and does not change any project details'
    : 'Archiving does not change your project, but archived project are hidden by default in the project list';

  const handleClickArchive = () => {
    if (props.project.isArchived) {
      return mutate({ isArchived: false });
    }

    mutate({ isArchived: true });
  };

  const handleRemoveWorkflow = () => {
    mutate(
      {
        workflowId: null,
        workflowStageId: null,
      },
      {
        onSuccess: () => {
          // Invalidate the projects list query to refresh the list
          queryClient.invalidateQueries({ queryKey: ['projects'] });
          queryClient.invalidateQueries({ queryKey: props.queryKey });
          menu.close();
        },
      }
    );
  };

  const { showToast } = useToast();
  const queryClient = useQueryClient();

  const resyncProjectMutation = useMutation({
    mutationFn: (parameters: HubspotResyncProject) => {
      showToast({
        duration: 5000,
        element: <Box>Sync in progress, project will update when complete</Box>,
      });
      return hubSpotResyncProject(parameters);
    },
    onSuccess: () => {
      menu.close();
      queryClient.invalidateQueries({
        queryKey: props.queryKey,
      });
    },
  });

  const resyncSfdcProjectMutation = useMutation({
    mutationFn: (parameters: SfdcResyncProject) => {
      showToast({
        duration: 5000,
        element: <Box>Sync in progress, project will update when complete</Box>,
      });
      return sfdcResyncProject(parameters);
    },
    onSuccess: () => {
      menu.close();
      queryClient.invalidateQueries({
        queryKey: props.queryKey,
      });
    },
    onError: (error: unknown) => {
      menu.close();
      if (error instanceof AxiosError && error.response?.status === 404) {
        showToast({
          duration: 5000,
          element: (
            <Box>
              Project not found in Salesforce. Please check the CRM connection.
            </Box>
          ),
        });
      } else {
        showToast({
          duration: 5000,
          element: (
            <Box>
              Failed to queue resync. Please try again or contact support.
            </Box>
          ),
        });
      }
    },
  });

  return (
    <>
      <CreateProjectDialog
        isOpen={createDialog.isOpen}
        renewal={props.project}
        onClose={createDialog.close}
      />
      <DeleteProjectDialog
        isOpen={deleteDialog.isOpen}
        project={props.project}
        onClose={deleteDialog.close}
      />
      <EnableWorkflowDialog
        isOpen={enableWorkflowDialog.isOpen}
        onClose={enableWorkflowDialog.close}
        project={props.project}
        queryKey={props.queryKey}
      />
      <Button
        ref={ref}
        variant="contained"
        color="secondary"
        onClick={menu.open}
        endIcon={<ArrowDropDownRounded fontSize="small" />}
      >
        More actions
      </Button>
      <Menu
        open={menu.isOpen}
        onClose={menu.close}
        onClick={menu.close}
        anchorEl={ref.current}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        MenuListProps={{ disablePadding: true }}
        slotProps={{
          paper: { sx: { maxWidth: '100vw', width: '25rem', marginTop: 0.5 } },
        }}
      >
        {props.project.activeWorkflow ? (
          <ActionMenuItem
            icon={<StopRounded color="action" fontSize="small" />}
            label="Disable workflow"
            description="Turn off the current workflow to stop tracking this project through stages"
            onClick={handleRemoveWorkflow}
            isDisabled={isPending}
          />
        ) : (
          <ActionMenuItem
            icon={<PlayArrowRounded color="action" fontSize="small" />}
            label="Enable workflow"
            description="Turn on a workflow for this project to start tracking progress through stages"
            onClick={enableWorkflowDialog.open}
          />
        )}
        <ActionMenuItem
          icon={<RefreshRounded color="action" fontSize="small" />}
          label="Renew project"
          description="Renewing creates a new project that refers back to this one as the previous project"
          onClick={createDialog.open}
        />
        {props.isCRMEnabled &&
          (props.project.integrations[0]?.id === 'HUBSPOT' ||
            props.project.integrations[0]?.id === 'SFDC') && (
            <ActionMenuItem
              icon={<Sync color="action" fontSize="small" />}
              label="Resync project"
              isDisabled={
                resyncProjectMutation.isPending ||
                resyncSfdcProjectMutation.isPending ||
                (props.project.integrations[0]?.isSyncing ?? false)
              }
              tooltip={
                props.project.integrations[0]?.isSyncing
                  ? `This is currently being re-synchronized from ${props.project.integrations[0]?.id === 'HUBSPOT' ? 'HubSpot' : 'Salesforce'}`
                  : undefined
              }
              description={`Update this project with the latest data from ${props.project.integrations[0]?.id === 'HUBSPOT' ? 'HubSpot' : 'Salesforce'}`}
              onClick={() => {
                if (props.project.integrations[0]?.id === 'HUBSPOT') {
                  resyncProjectMutation.mutate({
                    projectId: props.project.id,
                  });
                } else {
                  resyncSfdcProjectMutation.mutate({
                    projectKey: props.project.key,
                  });
                }
              }}
            />
          )}
        <ActionMenuItem
          icon={<DownloadRounded color="action" fontSize="small" />}
          label="Export project"
          description="Export the details of this specific project to an Excel (.xlsx) file"
          onClick={() => {
            const url = new URL(
              `/v1/export/projects/${props.project.key}`,
              import.meta.env.VITE_API_URL
            );
            window.open(url, '_blank');
          }}
        />
        <ActionMenuItem
          isDisabled={isPending}
          onClick={handleClickArchive}
          label={archiveControlTitle}
          description={archiveControlDescription}
          icon={
            <ArchivedIcon
              color="action"
              isArchived={props.project.isArchived}
            />
          }
        />
        <ActionMenuItem
          icon={<Delete color="action" fontSize="small" />}
          label="Delete project"
          description="Delete this project and all associated data"
          onClick={deleteDialog.open}
        />
      </Menu>
    </>
  );
}
